import React from 'react';

import { useQuery, useMutation } from 'react-query';

import Library from '../../components/Library';
import FolderService from '../../services/FolderService';
import MeetingService from '../../services/MeetingService';
import useFetchingState from '../../components/Library/useFetchingState';
import { useUiContext } from '../../context/UiContext';

const itemsRange = 999999;

async function runWithConcurrency(items, concurrency, task) {
  if (!items?.length) return [];

  const results = [];
  let index = 0;

  const workers = Array.from({
    length: Math.min(concurrency, items.length),
  }).map(async () => {
    while (index < items.length) {
      const currentIndex = index;
      index += 1;
      // eslint-disable-next-line no-await-in-loop
      results[currentIndex] = await task(items[currentIndex]);
    }
  });

  await Promise.all(workers);
  return results;
}

export default function TrashBinPage() {
  const { notifyError, notifySuccess, showConfirmActionModal } = useUiContext();

  const {
    page,
    setPage,
    searchInput,
    setSearchInput,
    sortField,
    setSortField,
    sortOrder,
    setSortOrder,
    debouncedSearchInput,
    isShare,
    id,
    isMobile,
  } = useFetchingState();

  const {
    data: folders,
    refetch: refetchFolders,
    isFetching: areFoldersFetching,
  } = useQuery(
    ['folders', page, debouncedSearchInput, sortField, sortOrder],
    () =>
      FolderService.getDeletedFolders({
        page,
        search: debouncedSearchInput,
        limit: itemsRange,
        sortField,
        sortOrder,
        deleted: true,
      }),
    {
      keepPreviousData: true,
      enabled: !id,
    }
  );

  const { data: deletedMeetingsData, refetch: refetchDeletedMeetings } =
    useQuery(
      [
        'meetings',
        page,
        debouncedSearchInput,
        itemsRange,
        sortField,
        sortOrder,
      ],
      () =>
        MeetingService.getMeetings({
          page,
          search: debouncedSearchInput,
          limit: itemsRange,
          type: 'my videos',
          sortField,
          sortOrder,
          deleted: true,
        }),
      {
        keepPreviousData: true,
        //   refetchInterval: processingMeetings.length ? 2500 : 0,
      }
    );

  const deletedMeetings = deletedMeetingsData?.rows || deletedMeetingsData;

  const { mutate: emptyTrashMutation, isLoading: isEmptyingTrash } = useMutation(
    async ({ folderIds, meetingIds }) => {
      const errors = [];

      await runWithConcurrency(folderIds, 5, async (folderId) => {
        try {
          await FolderService.deleteFolder(folderId);
        } catch (error) {
          if (error?.response?.status !== 404) {
            errors.push({ type: 'folder', id: folderId, error });
          }
        }
      });

      await runWithConcurrency(meetingIds, 5, async (meetingId) => {
        try {
          await MeetingService.deleteMeeting(meetingId);
        } catch (error) {
          if (error?.response?.status !== 404) {
            errors.push({ type: 'meeting', id: meetingId, error });
          }
        }
      });

      return { errors };
    },
    {
      onSuccess: ({ errors }) => {
        refetchFolders();
        refetchDeletedMeetings();

        if (!errors?.length) {
          notifySuccess('Trash emptied');
          return;
        }

        notifyError(
          `Some items could not be deleted (${errors.length}). Please try again.`
        );
      },
      onError: (error) => {
        notifyError(
          error?.response?.data?.message || 'Failed to empty trash bin'
        );
      },
    }
  );

  const { mutate: deleteFolderMutation } = useMutation(
    FolderService.deleteFolder,
    {
      onSuccess: () => {
        refetchFolders();
      },
    }
  );

  const { mutate: toggleFolderTrashStatusMutation } = useMutation(
    FolderService.toggleFolderTrashStatus,
    {
      onSuccess: () => {
        refetchFolders();
        refetchDeletedMeetings();
      },
    }
  );

  const { mutate: toggleMeetingTrashStatusMutation } = useMutation(
    MeetingService.toggleMeetingTrashStatus,
    {
      onSuccess: () => {
        refetchDeletedMeetings();
      },
    }
  );

  const { mutate: updateFolderMutation } = useMutation(
    FolderService.updateFolder,
    {
      onSuccess: () => {
        refetchFolders();
        refetchDeletedMeetings();
      },
      onError: (error) => {
        notifyError(
          error?.response?.data?.message || 'Failed to update folder'
        );
      },
    }
  );

  const { mutate: deleteMeetingMutation } = useMutation(
    MeetingService.deleteMeeting,
    {
      onSuccess: () => {
        refetchDeletedMeetings();
      },
      onError: (error) => {
        notifyError(
          error?.response?.data?.message || 'Failed to delete video'
        );
      },
    }
  );

  const folderRows = folders?.rows || [];
  const meetingRows = deletedMeetings || [];
  const totalTrashItemsCount = folderRows.length + meetingRows.length;

  const handleEmptyTrash = () => {
    if (!totalTrashItemsCount || isEmptyingTrash) return;

    const folderIds = folderRows.map((folder) => folder.id);
    const meetingIds = meetingRows.map((meeting) => meeting.id);

    showConfirmActionModal({
      title: 'Empty trash?',
      message:
        'This will permanently delete everything currently shown in your Trash Bin. This action can’t be undone.',
      onConfirm: () => emptyTrashMutation({ folderIds, meetingIds }),
    });
  };

  return (
    <Library
      searchInput={searchInput}
      setSearchInput={setSearchInput}
      isMobile={isMobile}
      page={page}
      setPage={setPage}
      sortField={sortField}
      setSortField={setSortField}
      sortOrder={sortOrder}
      setSortOrder={setSortOrder}
      debouncedSearchInput={debouncedSearchInput}
      refetchFolders={refetchFolders}
      //  refetchFolder={refetchFolder}
      areFoldersFetching={areFoldersFetching}
      //  isFolderFetching={isFolderFetching}
      folders={folders}
      openedFolder={null}
      id={id}
      isShare={isShare}
      itemsRange={itemsRange}
      deleteFolderMutation={deleteFolderMutation}
      moveToTrashMeetingMutation={toggleMeetingTrashStatusMutation}
      restoreMeetingMutation={toggleMeetingTrashStatusMutation}
      updateFolderMutation={updateFolderMutation}
      breadcrumbLabel="Trash Bin"
      breadcrumbTo="/trash-bin"
      deletedMeetings={deletedMeetings}
      deleteMeetingMutation={deleteMeetingMutation}
      restoreFolderMutation={toggleFolderTrashStatusMutation}
      onEmptyTrash={handleEmptyTrash}
      isEmptyTrashDisabled={!totalTrashItemsCount || isEmptyingTrash}
      emptyTrashLabel={isEmptyingTrash ? 'Emptying…' : 'Empty trash'}
    />
  );
}
